import {
  DocumentPurchaseModel,
  DocumentTypeEnum,
} from 'api-hooks/order/travel-document';
import { TravelDocumentSalesModel } from 'api-hooks/sale/travel-document';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { VisaDocumentModel } from 'api-hooks/visa-document';
import { generateId } from 'common/utils/string';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import React from 'react';

import {
  DocumentPurchaseFormType,
  DocumentPurchaseItemAddOnsFormType,
  DocumentPurchaseItemFormType,
  DocumentPurchaseItemGuestDetailFormType,
  DocumentPurchaseItemGuestFormType,
} from '../components/form-type';

interface Props {
  documentPurchase?: DocumentPurchaseModel;
  documentSale?: TravelDocumentSalesModel;
  defaultValues?: DocumentPurchaseFormType;
}

export function addNewDocumentPurchaseAddOns(): DocumentPurchaseItemAddOnsFormType {
  return {
    customId: generateId(),
    id: undefined,
    description: '',
    guestId: '',
    guestName: '',
    price: undefined,
  } as never as DocumentPurchaseItemAddOnsFormType;
}

export function addNewDocumentPurchaseGuestDetail(): DocumentPurchaseItemGuestDetailFormType {
  return {
    appointmentAt: new Date(),
    biometric: false,
    coverArea: '',
    customId: generateId(),
    days: 1,
    description: '',
    endedAt: new Date(),
    id: undefined,
    package: '',
    price: undefined,
    startedAt: new Date(),
    type: '',
    visaDocumentId: '',
    company: '',
  } as never as DocumentPurchaseItemGuestDetailFormType;
}

export function addNewDocumentPurchaseGuest(): DocumentPurchaseItemGuestFormType {
  return {
    customId: generateId(),
    id: undefined,
    details: [addNewDocumentPurchaseGuestDetail()],
    guestId: '',
    guestName: '',
    // additional information except visa
    company: '',
    coverArea: '',
    days: 0,
    description: '',
    endedAt: null,
    package: '',
    price: 0,
    startedAt: null,
    type: '',
  } as never as DocumentPurchaseItemGuestFormType;
}

export function addNewDocumentPurchase(): DocumentPurchaseItemFormType {
  return {
    customId: generateId(),
    id: undefined,
    documentPurchaseAddOns: [],
    documentPurchaseGuests: [addNewDocumentPurchaseGuest()],
    documentType: DocumentTypeEnum.visa,
    visaDocuments: [],
  } as never as DocumentPurchaseItemFormType;
}

export function visaDocumentToDocumentPurchase(
  visaDocument: VisaDocumentModel,
) {
  const documentPurchases: DocumentPurchaseFormType['documentPurchases'] = [
    {
      customId: generateId(),
      documentType: DocumentTypeEnum.visa,
      id: undefined,
      documentPurchaseGuests: [
        {
          ...addNewDocumentPurchaseGuest(),
          details: [
            {
              ...addNewDocumentPurchaseGuestDetail(),
              price: visaDocument.price,
              type: 'visa',
              label: visaDocument.name,
              visaDocumentId: visaDocument.id,
            },
          ],
        },
      ],
      documentPurchaseAddOns: [],
      visaDocuments: [
        {
          id: visaDocument.id,
          label: visaDocument.name,
          extra: visaDocument,
        } as any,
      ],
    },
  ];
  const transactionAt = new Date();
  const defaultValues: DocumentPurchaseFormType = {
    agentId: '',
    agentStatus: '',
    remainingTransactionQuota: null,
    isExceedingQuota: false,
    code: '',
    commission: 0,
    description: '',
    discount: 0,
    invoiceId: '',
    invoiceRefId: '',
    needApproval: false,
    otherCost: 0,
    paymentChannelId: '',
    refId: undefined,
    parentRefId: '',
    tempTransactionAt: transactionAt,
    transactionAt,
    tourId: '',
    tourRefId: '',
    type: 'sales',
    documentSale: undefined,
    documentPurchases,
    orderNumber: '',
    saleTotal: 0,
    total: 0,
    documentPurchase: undefined,
  };

  return defaultValues;
}

export function tourListToDocumentPurchase(tourList: TourModel) {
  const transactionAt = new Date();
  const defaultValues: DocumentPurchaseFormType = {
    agentId: '',
    agentStatus: '',
    remainingTransactionQuota: null,
    isExceedingQuota: false,
    code: '',
    commission: 0,
    description: '',
    discount: 0,
    invoiceId: '',
    invoiceRefId: '',
    needApproval: false,
    otherCost: 0,
    paymentChannelId: '',
    refId: undefined,
    parentRefId: '',
    tempTransactionAt: transactionAt,
    transactionAt,
    tourId: tourList.id,
    tourRefId: tourList.id,
    type: 'tour',
    documentSale: undefined,
    documentPurchases: [addNewDocumentPurchase()], //document purchases will be fill with select sales
    orderNumber: '',
    saleTotal: 0,
    total: 0,
    documentPurchase: undefined,
  };

  return defaultValues;
}

export default function useGetDefaultValues(props: Props) {
  const { documentPurchase, documentSale } = props;
  const defaultValues = React.useMemo<DocumentPurchaseFormType>(() => {
    const transactionAt = documentPurchase?.transactionAt || new Date();

    if (props.defaultValues) return props.defaultValues;

    if (documentSale) {
      const result = {
        orderNumber: '',
        saleTotal: documentSale.total,
        agentId: '',
        agentStatus: '',
        remainingTransactionQuota: null,
        isExceedingQuota: false,
        code: documentSale.code || '',
        commission: 0,
        description: '',
        discount: documentSale.discount || 0,
        invoiceId: documentSale.id,
        invoiceRefId: documentSale.refId,
        needApproval: false,
        otherCost: documentSale.otherCost || 0,
        paymentChannelId: '',
        refId: undefined,
        parentRefId: '',
        tempTransactionAt: transactionAt,
        transactionAt,
        tourRefId: '',
        type: 'sales',
        documentSale,
        documentPurchase,
        total: 0,
        tourId: '',
        documentPurchases: [addNewDocumentPurchase()], //document purchases will be fill with select sales
      };

      return result;
    }
    const agent = documentPurchase?.agent;
    const paymentChannel = documentPurchase?.paymentChannel;
    const sale = documentPurchase?.sale;
    const tour = documentPurchase?.tour;

    // const visaDocumentOptions =
    //   documentPurchase?.documentPurchases
    //     ?.map((ds) => {
    //       return (ds.visaDocuments || [])
    //         .map((vd) => {
    //           return {
    //             id: vd.id,
    //             label: vd.name,
    //             visaDocumentId: vd.id,
    //             price: vd.price || 0,
    //             preferenceId: vd.preference.id,
    //           };
    //           // MULTIPRICE
    //           // return (vd.visaDocumentItems || []).map((vdi) => {
    //           //   const option = {
    //           //     id: vdi.id,
    //           //     label: [vd.code, vd.name, vdi.preference.name].join(' - '),
    //           //     preferenceId: vdi.preference.id,
    //           //     price: vdi.price,
    //           //     visaDocumentItemId: vdi.id,
    //           //   };
    //           //   return option;
    //           // });
    //         })
    //         .flat();
    //     })
    //     .flat() ?? [];

    return {
      agentId: agent?.id || '',
      agentStatus: agent?.status || '',
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      paymentChannelId: paymentChannel?.id || '',
      code: documentPurchase?.code || '',
      orderNumber: documentPurchase?.number || '',
      commission: documentPurchase?.agentCommission?.amount || 0,
      description: documentPurchase?.description || '',
      discount: documentPurchase?.discount || 0,
      refId: documentPurchase?.refId,
      parentRefId: documentPurchase?.parentPurchase?.id,
      invoiceId: sale?.id,
      tourId: tour?.id,
      tourRefId: documentPurchase?.tour?.refId || '',
      invoiceRefId: documentPurchase?.invoiceRefId || '',
      needApproval: false,
      transactionAt,
      tempTransactionAt: transactionAt,
      otherCost: documentPurchase?.otherCost || 0,
      total: documentPurchase?.total || 0,
      saleTotal: sale?.total || 0,
      type: documentPurchase?.type || 'sales',
      documentPurchases: documentPurchase?.documentPurchases?.map(
        (dp, index) => {
          const documentPurchaseAddOns: DocumentPurchaseItemAddOnsFormType[] =
            dp.documentPurchaseAddOns.map((dpao) => {
              const guest = dpao.guest;
              return {
                customId: generateId(),
                id: dpao.id,
                description: dpao.description || '',
                guestId: guest.id,
                guestName: guestNameTransformer(guest),
                price: dpao.price || 0,
              };
            });

          const documentPurchase: DocumentPurchaseItemFormType = {
            customId: generateId(),
            id: dp.id,
            documentType: dp.documentType,
            visaDocuments: (dp.visaDocuments || []).map((vd) => {
              return {
                id: vd.id,
                label: [vd.code, vd.name].join(' - '),
                extra: vd as any,
              };
            }),
            documentPurchaseAddOns,
            documentPurchaseGuests: [],
          };

          if (dp.documentType === DocumentTypeEnum.visa) {
            const documentPurchaseGuests: DocumentPurchaseItemGuestFormType[] =
              dp.documentPurchaseVisaGuests.map((dpvg) => {
                const guest = dpvg.guest;

                const details: DocumentPurchaseItemGuestDetailFormType[] =
                  dpvg.documentPurchaseVisaGuestDetails.map((dpvgd) => {
                    const visaDocument = dpvgd.visaDocument;
                    return {
                      id: dpvgd.id,
                      biometric: dpvgd.biometric,
                      price: dpvgd.price || 0,
                      appointmentAt: dpvgd.appointmentAt,
                      description: dpvgd.description || '',
                      coverArea: '',
                      customId: generateId(),
                      days: 1,
                      endedAt: new Date(),
                      package: '',
                      startedAt: new Date(),
                      type: '',
                      visaDocumentId: visaDocument?.id || '',
                      company: '',
                      label: visaDocument?.name || '',
                    };
                  });

                return {
                  id: dpvg.id,
                  customId: generateId(),
                  guestId: guest.id,
                  guestName: guestNameTransformer(guest),
                  //
                  details,
                  company: '',
                  coverArea: '',
                  days: 1,
                  description: '',
                  endedAt: new Date(),
                  package: '',
                  price: 0,
                  startedAt: new Date(),
                  type: '',
                };
              });
            return { ...documentPurchase, documentPurchaseGuests };
          }
          if (dp.documentType === DocumentTypeEnum.passport) {
            const documentPurchaseGuests: DocumentPurchaseItemGuestFormType[] =
              dp.documentPurchasePassportGuests.map((dppg) => {
                const guest = dppg.guest;

                const detail: DocumentPurchaseItemGuestDetailFormType = {
                  id: dppg.details.id,
                  type: dppg.details.type || '',
                  description: dppg.details.description || '',
                  price: dppg.details.price || 0,
                  appointmentAt: new Date(),
                  biometric: false,
                  coverArea: '',
                  customId: generateId(),
                  days: 1,
                  endedAt: new Date(),
                  package: '',
                  startedAt: new Date(),
                  visaDocumentId: '',
                  company: '',
                  label: '',
                };
                return {
                  customId: generateId(),
                  details: [detail],
                  guestId: guest.id,
                  guestName: guestNameTransformer(guest),
                  id: dppg.id,
                  company: detail.company,
                  coverArea: detail.coverArea,
                  days: detail.days,
                  description: detail.description,
                  endedAt: detail.endedAt,
                  package: detail.package,
                  price: detail.price,
                  startedAt: detail.startedAt,
                  type: detail.type,
                };
              });
            return { ...documentPurchase, documentPurchaseGuests };
          }
          if (dp.documentType === DocumentTypeEnum.insurance) {
            const documentPurchaseGuests: DocumentPurchaseItemGuestFormType[] =
              dp.documentPurchaseInsuranceGuests.map((dpig) => {
                const guest = dpig.guest;
                const details: DocumentPurchaseItemGuestDetailFormType[] =
                  dpig.details.map((detail) => {
                    return {
                      company: detail.company || '',

                      coverArea: detail.coverArea || '',
                      customId: generateId(),
                      days: detail.days || 1,
                      description: detail.description || '',
                      endedAt: detail.endedAt || new Date(),
                      id: detail.id,
                      package: detail.package || '',
                      price: detail.price || 0,
                      startedAt: detail.startedAt || new Date(),
                      type: detail.type || '',
                      visaDocumentId: '',
                      appointmentAt: new Date(),
                      label: '',
                      biometric: false,
                    };
                  });
                const detail = details[0];
                return {
                  customId: generateId(),
                  details,
                  guestId: guest.id,
                  guestName: guestNameTransformer(guest),
                  id: dpig.id,
                  company: detail.company,
                  coverArea: detail.coverArea,
                  days: detail.days,
                  description: detail.description,
                  endedAt: detail.endedAt,
                  package: detail.package,
                  price: detail.price,
                  startedAt: detail.startedAt,
                  type: detail.type,
                };
              });
            return { ...documentPurchase, documentPurchaseGuests };
          }
          if (dp.documentType === DocumentTypeEnum.apec_card) {
            const documentPurchaseGuests: DocumentPurchaseItemGuestFormType[] =
              dp.documentPurchaseApecCardGuests.map((dpacg) => {
                const guest = dpacg.guest;

                const detail: DocumentPurchaseItemGuestDetailFormType = {
                  appointmentAt: new Date(),
                  id: dpacg.details.id,
                  type: dpacg.details.type,
                  description: dpacg.details.description,
                  price: dpacg.details.price,
                  biometric: false,
                  company: '',
                  coverArea: '',
                  customId: generateId(),
                  days: 1,
                  endedAt: new Date(),
                  package: '',
                  startedAt: new Date(),
                  visaDocumentId: '',
                  label: '',
                };

                return {
                  id: dpacg.id,
                  customId: generateId(),
                  details: [detail],
                  guestId: guest.id,
                  guestName: guestNameTransformer(guest),
                  company: detail.company,
                  coverArea: detail.coverArea,
                  days: detail.days,
                  description: detail.description,
                  endedAt: detail.endedAt,
                  package: detail.package,
                  price: detail.price,
                  startedAt: detail.startedAt,
                  type: detail.type,
                };
              });
            return { ...documentPurchase, documentPurchaseGuests };
          }
          return documentPurchase;
        },
      ) ?? [addNewDocumentPurchase()],

      documentPurchase,
      documentSale,
    };
  }, [documentPurchase, documentSale, props.defaultValues]);

  return defaultValues;
}
